import { ReactiveElement } from 'lit'
import { property } from 'lit/decorators.js'

type Constructor<T = Record<string, unknown>> = {
  new (...args: any[]): T
  prototype: T
}

type DisableableElement = HTMLElement & { disabled?: boolean }

export interface FocusableElementInterface {
  disabled: boolean
  autofocus: boolean
  focusElement: DisableableElement
  focus(options?: FocusOptions): void
  blur(): void
  click(): void
}

export function FocusableMixin<T extends Constructor<ReactiveElement>>(
  constructor: T,
): T & Constructor<FocusableElementInterface> {
  class FocusableElement extends constructor {
    @property({ type: Boolean, reflect: true })
    public disabled = false
    @property({ type: Boolean })
    public override autofocus = false

    get focusElement(): DisableableElement {
      throw new Error('Must implement focusElement getter!')
    }

    public override focus(options?: FocusOptions): void {
      if (this.disabled || !this.focusElement) {
        return
      }

      if (this.focusElement !== this) {
        this.focusElement.focus(options)
      } else {
        HTMLElement.prototype.focus.apply(this, [options])
      }
    }

    public override blur(): void {
      const focusElement = this.focusElement || this
      if (focusElement !== this) {
        focusElement.blur()
      } else {
        HTMLElement.prototype.blur.apply(this)
      }
    }

    public override click(): void {
      if (this.disabled) {
        return
      }

      const focusElement = this.focusElement || this
      if (focusElement !== this) {
        focusElement.click()
      } else {
        HTMLElement.prototype.click.apply(this)
      }
    }
  }

  return FocusableElement as Constructor<FocusableElementInterface> & T
}
